import type { NotificationChannelMatrixDto } from '@up2stay/up2promo-api-client';
import { Switch, cn } from '@up2stay/up2promo-ui';
import { useMemo } from 'react';

import type {
  NotificationChannelKey,
  NotificationPreferencesTableDict,
  NotificationPreferenceTableRow,
} from '@/core/components/notifications/notification-preferences-table.types';
import {
  SettingsGroup,
  SettingsGroupLabel,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import {
  resolveNotificationDomainLabel,
  sortDomainKeys,
} from '@/core/lib/notification-type-i18n';
import {
  applyPushChannelChange,
  ensurePushPermissionForBulkEnable,
} from '@/core/lib/push-preference-guard';

const CHANNEL_KEYS: NotificationChannelKey[] = [
  'email',
  'inApp',
  'sms',
  'push',
];

type NotificationPreferencesTableProps = {
  rows: NotificationPreferenceTableRow[];
  dict: NotificationPreferencesTableDict;
  showPush: boolean;
  onRowsChange: (rows: NotificationPreferenceTableRow[]) => void;
  onPersist: (rows: NotificationPreferenceTableRow[]) => Promise<void>;
};

function groupRowsByDomain(rows: NotificationPreferenceTableRow[]) {
  const grouped = new Map<string, NotificationPreferenceTableRow[]>();
  for (const row of rows) {
    const list = grouped.get(row.domain) ?? [];
    list.push(row);
    grouped.set(row.domain, list);
  }
  return grouped;
}

function getChannelsMasterState(
  channels: NotificationChannelMatrixDto,
  visibleChannels: NotificationChannelKey[],
): 'all' | 'none' | 'some' {
  const enabledCount = visibleChannels.filter(
    (channel) => channels[channel],
  ).length;
  if (enabledCount === 0) return 'none';
  if (enabledCount === visibleChannels.length) return 'all';
  return 'some';
}

export function NotificationPreferencesTable({
  rows,
  dict,
  showPush,
  onRowsChange,
  onPersist,
}: NotificationPreferencesTableProps) {
  const visibleChannels = useMemo(
    () => (showPush ? CHANNEL_KEYS : CHANNEL_KEYS.filter((k) => k !== 'push')),
    [showPush],
  );

  const grouped = useMemo(() => groupRowsByDomain(rows), [rows]);
  const domainKeys = useMemo(
    () => sortDomainKeys([...grouped.keys()]),
    [grouped],
  );

  const pushLabels = {
    denied: dict.pushDenied,
    unavailable: dict.pushUnavailable,
  };

  const persist = async (nextRows: NotificationPreferenceTableRow[]) => {
    onRowsChange(nextRows);
    await onPersist(nextRows);
  };

  const updateRow = async (
    typeId: string,
    updater: (
      row: NotificationPreferenceTableRow,
    ) => NotificationPreferenceTableRow,
  ) => {
    const nextRows = rows.map((row) =>
      row.typeId === typeId ? updater(row) : row,
    );
    await persist(nextRows);
  };

  const handleAllChannelsChange = async (typeId: string, enabled: boolean) => {
    const row = rows.find((item) => item.typeId === typeId);
    if (!row) return;

    if (enabled && visibleChannels.includes('push') && !row.channels.push) {
      const allowed = await ensurePushPermissionForBulkEnable(pushLabels);
      if (!allowed) return;
    }

    let nextChannels: NotificationChannelMatrixDto = { ...row.channels };
    for (const channel of visibleChannels) {
      nextChannels[channel] = enabled;
    }

    if (enabled && visibleChannels.includes('push')) {
      nextChannels = await applyPushChannelChange({
        previous: row.channels,
        next: nextChannels,
        labels: pushLabels,
      });
    }

    await updateRow(typeId, (current) => ({
      ...current,
      channels: nextChannels,
    }));
  };

  const handleChannelChange = async (
    typeId: string,
    channel: NotificationChannelKey,
    checked: boolean,
  ) => {
    const row = rows.find((item) => item.typeId === typeId);
    if (!row) return;

    let nextChannels: NotificationChannelMatrixDto = {
      ...row.channels,
      [channel]: checked,
    };

    if (channel === 'push') {
      nextChannels = await applyPushChannelChange({
        previous: row.channels,
        next: nextChannels,
        labels: pushLabels,
      });
    }

    await updateRow(typeId, (current) => ({
      ...current,
      channels: nextChannels,
    }));
  };

  return (
    <div className="flex flex-col gap-6 md:gap-7">
      {domainKeys.map((domain) => {
        const domainRows = grouped.get(domain) ?? [];
        return (
          <div key={domain} className="flex flex-col gap-2">
            <SettingsGroupLabel>
              {resolveNotificationDomainLabel(domain, dict.domains)}
            </SettingsGroupLabel>
            <SettingsGroup>
              <SettingsGroupRows>
                {domainRows.map((row, index) => {
                  const masterState = getChannelsMasterState(
                    row.channels,
                    visibleChannels,
                  );

                  return (
                    <SettingsGroupRow
                      key={row.typeId}
                      separated={index > 0}
                      className="flex flex-col gap-3"
                    >
                      <div className="flex min-w-0 items-center gap-0.5">
                        <p className="truncate text-[13.5px] font-semibold leading-tight text-foreground">
                          {row.label}
                        </p>
                        {row.description ? (
                          <SettingsInfoHint label={row.label}>
                            {row.description}
                          </SettingsInfoHint>
                        ) : null}
                      </div>

                      <div className="flex flex-col gap-2.5">
                        <PreferenceSwitchRow
                          id={`${row.typeId}-all-channels`}
                          label={dict.allChannels}
                          checked={masterState === 'all'}
                          indeterminate={masterState === 'some'}
                          onCheckedChange={(checked) =>
                            void handleAllChannelsChange(row.typeId, checked)
                          }
                        />

                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                          {visibleChannels.map((channel) => (
                            <PreferenceSwitchRow
                              key={`${row.typeId}-${channel}`}
                              id={`${row.typeId}-${channel}`}
                              label={dict.channels[channel]}
                              checked={row.channels[channel]}
                              subdued
                              onCheckedChange={(checked) =>
                                void handleChannelChange(
                                  row.typeId,
                                  channel,
                                  checked,
                                )
                              }
                            />
                          ))}
                        </div>
                      </div>
                    </SettingsGroupRow>
                  );
                })}
              </SettingsGroupRows>
            </SettingsGroup>
          </div>
        );
      })}
    </div>
  );
}

function PreferenceSwitchRow({
  id,
  label,
  checked,
  indeterminate,
  subdued,
  onCheckedChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  subdued?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3',
        // Channel cells in the desktop grid: switch first, label right next
        // to it, so no switch sits beside the next channel's label (DEV-542).
        subdued && 'sm:flex-row-reverse sm:justify-end',
      )}
    >
      <label
        htmlFor={id}
        className={
          subdued
            ? 'text-[13px] text-muted-foreground'
            : 'text-[13.5px] font-medium text-foreground'
        }
      >
        {label}
      </label>
      <Switch
        id={id}
        checked={indeterminate ? false : checked}
        aria-checked={indeterminate ? 'mixed' : checked}
        className={
          indeterminate
            ? 'bg-primary/45 data-[state=unchecked]:bg-primary/45 [&>span]:translate-x-2.5'
            : undefined
        }
        onCheckedChange={(value) => {
          if (indeterminate) {
            onCheckedChange(true);
            return;
          }
          onCheckedChange(value);
        }}
      />
    </div>
  );
}
